/**
 * Minimum Defence Frequency (MDF) quiz.
 *
 * MDF is "the minimum % of your range you must defend (call or raise,
 * not fold) against villain's bet for villain's bluffs to break even
 * with zero equity." If you fold below MDF, an unbalanced bluffer
 * profits by betting any two cards.
 *
 *   MDF = pot / (pot + bet) = 1 / (1 + bet_fraction)
 *
 * Canonical reference points:
 *   33% pot bet  → MDF ≈ 75%
 *   50% pot bet  → MDF ≈ 67%
 *   66% pot bet  → MDF ≈ 60%
 *   75% pot bet  → MDF ≈ 57%
 *   pot-sized    → MDF = 50%
 *   150% overbet → MDF ≈ 40%
 *   2x overbet   → MDF = 33%
 *
 * Larger bets demand fewer defends. The complement of pot odds (which
 * gives the *caller's* equity threshold): MDF tells the *defender* how
 * much of their range to keep in.
 *
 * Procedural questions: pick a clean bet/pot ratio, the answer is the
 * MDF for that size. Multiple choice from canonical MDF tier set.
 */

import { pickFrom, shuffle } from "@/features/quiz/lib/common";

interface BetTier {
  fraction: number;
  /** Pot sizes that yield an integer bet at this fraction. */
  cleanPots: readonly number[];
}

const BET_TIERS: readonly BetTier[] = [
  { fraction: 1 / 3, cleanPots: [12, 15, 18, 24, 30, 60, 90] },
  { fraction: 0.5, cleanPots: [10, 20, 30, 40, 50, 60, 80, 100] },
  { fraction: 2 / 3, cleanPots: [12, 15, 18, 30, 60, 90] },
  { fraction: 0.75, cleanPots: [12, 16, 20, 40, 60, 80, 100] },
  { fraction: 1.0, cleanPots: [10, 20, 30, 40, 50, 60, 80, 100] },
  { fraction: 1.5, cleanPots: [10, 20, 30, 40, 60] },
  { fraction: 2.0, cleanPots: [10, 20, 30, 40, 50, 60] },
];

/** Canonical MDF % values. Multiple choice draws from this set. */
export const CANONICAL_MDF_PCTS = [33, 40, 50, 57, 60, 67, 75] as const;

export interface MdfQuestion {
  pot: number;
  bet: number;
  betFraction: number;
  options: number[];
  correctIndex: number;
}

/** Exact MDF in percentage points. */
export function mdfPct(pot: number, bet: number): number {
  return (pot / (pot + bet)) * 100;
}

function snapToCanonical(value: number): number {
  let best: number = CANONICAL_MDF_PCTS[0]!;
  let bestDelta = Math.abs(value - best);
  for (const v of CANONICAL_MDF_PCTS) {
    const delta = Math.abs(value - v);
    if (delta < bestDelta) {
      best = v;
      bestDelta = delta;
    }
  }
  return best;
}

export function generateQuestion(
  rand: () => number = Math.random,
): MdfQuestion {
  const tier = pickFrom(BET_TIERS, rand);
  const pot = pickFrom(tier.cleanPots, rand);
  const bet = pot * tier.fraction;
  if (!Number.isInteger(bet)) {
    throw new Error(`Non-integer bet from tier ${tier.fraction} × ${pot}`);
  }
  const correct = snapToCanonical(mdfPct(pot, bet));

  const sorted = [...CANONICAL_MDF_PCTS]
    .filter((v) => v !== correct)
    .sort((a, b) => Math.abs(a - correct) - Math.abs(b - correct));
  const distractors = [sorted[0]!, sorted[1]!, sorted[sorted.length - 1]!];
  const options = shuffle([correct, ...distractors], rand);
  return {
    pot,
    bet,
    betFraction: tier.fraction,
    options,
    correctIndex: options.indexOf(correct),
  };
}

export function describeFraction(fraction: number): string {
  if (Math.abs(fraction - 1 / 3) < 0.05) return "33% pot";
  if (Math.abs(fraction - 0.5) < 0.05) return "half-pot";
  if (Math.abs(fraction - 2 / 3) < 0.05) return "two-thirds pot";
  if (Math.abs(fraction - 0.75) < 0.05) return "75% pot";
  if (Math.abs(fraction - 1.0) < 0.05) return "pot-sized";
  if (Math.abs(fraction - 1.5) < 0.1) return "150% overbet";
  if (Math.abs(fraction - 2.0) < 0.1) return "2x overbet";
  return `${Math.round(fraction * 100)}% pot`;
}
